/**
 * Arquivo: src/services/dataClient.ts
 * Versão: 6.0.0
 * Cliente tipado do gateway financeiro do Worker.
 */
import type {
  BaseFinanceira,
  Categoria,
  CartaoCredito,
  CompraCartao,
  Conta,
  FaturaCartao,
  Investimento,
  Lancamento,
  MovimentoInvestimento,
  ParcelaCartao,
  Recorrencia,
  Subcategoria,
  Transferencia,
} from '../types/database'

type GenericRow = Record<string, any>

type TableRows = {
  bases: BaseFinanceira
  categorias: Categoria
  cartoes: CartaoCredito
  compras_cartao: CompraCartao
  contas: Conta
  faturas: FaturaCartao
  investimentos: Investimento
  lancamentos: Lancamento
  mov_investimentos: MovimentoInvestimento
  parcelas_cartao: ParcelaCartao
  recorrencias: Recorrencia
  subcategorias: Subcategoria
  transferencias: Transferencia
}

type TableName = keyof TableRows
type RowFor<TTable extends string> = TTable extends TableName ? TableRows[TTable] : GenericRow

type Filter = { column:string; op:string; value?:unknown; innerOp?:string }
type Order = { column:string; ascending:boolean; nullsFirst?:boolean }
type Payload = {
  table:string
  action:'select'|'insert'|'update'|'delete'|'upsert'
  columns?:string
  values?:unknown
  filters:Filter[]
  orders:Order[]
  limit?:number
  range?:{from:number;to:number}
  mode?:'single'|'maybeSingle'
  returning?:string
  upsert?:{onConflict?:string;ignoreDuplicates?:boolean}
}

type Success<T> = { data:T; error:null; count?:number|null }
type Failure = { data:null; error:Error; count?:number|null }
type Result<T> = Success<T> | Failure

const asError=(value:unknown)=>
  value instanceof Error ? value : new Error(typeof value==='string' ? value : 'Falha ao acessar os dados.')

export function createDataClient(send:(payload:Payload)=>Promise<any>,sendRpc?:(name:string,args:Record<string,unknown>)=>Promise<any>,cacheEnabled=false){
const SELECT_CACHE_TTL_MS=cacheEnabled?20_000:0
const selectCache=new Map<string,{expires:number;value:any}>()
const selectInflight=new Map<string,Promise<any>>()

function clearSelectCache(){
  selectCache.clear()
  selectInflight.clear()
}

class QueryBuilder<TRecord extends GenericRow, TResult> implements PromiseLike<Result<TResult>> {
  private payload:Payload

  constructor(table:string){
    this.payload={table,action:'select',columns:'*',filters:[],orders:[]}
  }

  select(columns='*'){
    if(this.payload.action==='select') this.payload.columns=columns
    else this.payload.returning=columns
    return this
  }

  insert(values:unknown){this.payload.action='insert';this.payload.values=values;return this}
  update(values:unknown){this.payload.action='update';this.payload.values=values;return this}
  delete(){this.payload.action='delete';return this}
  upsert(values:unknown,options:{onConflict?:string;ignoreDuplicates?:boolean}={}){
    this.payload.action='upsert'
    this.payload.values=values
    this.payload.upsert=options
    return this
  }

  eq(column:string,value:unknown){this.payload.filters.push({column,op:'eq',value});return this}
  neq(column:string,value:unknown){this.payload.filters.push({column,op:'neq',value});return this}
  gt(column:string,value:unknown){this.payload.filters.push({column,op:'gt',value});return this}
  gte(column:string,value:unknown){this.payload.filters.push({column,op:'gte',value});return this}
  lt(column:string,value:unknown){this.payload.filters.push({column,op:'lt',value});return this}
  lte(column:string,value:unknown){this.payload.filters.push({column,op:'lte',value});return this}
  ilike(column:string,value:unknown){this.payload.filters.push({column,op:'ilike',value});return this}
  in(column:string,value:unknown[]){this.payload.filters.push({column,op:'in',value});return this}
  is(column:string,value:unknown){this.payload.filters.push({column,op:'is',value});return this}
  not(column:string,innerOp:string,value:unknown){this.payload.filters.push({column,op:'not',innerOp,value});return this}

  order(column:string,options:{ascending?:boolean;nullsFirst?:boolean}={}){
    this.payload.orders.push({
      column,
      ascending:options.ascending!==false,
      ...(typeof options.nullsFirst==='boolean'?{nullsFirst:options.nullsFirst}:{}),
    })
    return this
  }

  limit(value:number){this.payload.limit=value;return this}
  range(from:number,to:number){this.payload.range={from,to};return this}

  single():QueryBuilder<TRecord,TRecord>{
    this.payload.mode='single'
    return this as unknown as QueryBuilder<TRecord,TRecord>
  }

  maybeSingle():QueryBuilder<TRecord,TRecord|null>{
    this.payload.mode='maybeSingle'
    return this as unknown as QueryBuilder<TRecord,TRecord|null>
  }

  private async run():Promise<Result<TResult>>{
    try{
      const isSelect=this.payload.action==='select'
      const key=isSelect?JSON.stringify(this.payload):''
      const now=Date.now()
      if(isSelect){
        const hit=selectCache.get(key)
        if(hit&&hit.expires>now)return hit.value as Result<TResult>
        const pending=selectInflight.get(key)
        if(pending)return await pending as Result<TResult>
      }else{
        clearSelectCache()
      }

      const request=send(this.payload).then(body=>({data:(body?.data??null) as TResult,error:null,count:body?.count??null} as Result<TResult>))
        .catch(error=>({data:null,error:asError(error),count:null} as Result<TResult>))

      if(isSelect)selectInflight.set(key,request)
      const result=await request
      if(isSelect){
        selectInflight.delete(key)
        if(!result.error)selectCache.set(key,{expires:Date.now()+SELECT_CACHE_TTL_MS,value:result})
      }
      return result
    }catch(error){
      return{data:null,error:asError(error),count:null}
    }
  }

  then<A=Result<TResult>,B=never>(
    onfulfilled?:((value:Result<TResult>)=>A|PromiseLike<A>)|null,
    onrejected?:((reason:any)=>B|PromiseLike<B>)|null,
  ):PromiseLike<A|B>{
    return this.run().then(onfulfilled,onrejected)
  }
}

function from<TTable extends string>(table:TTable){
  type TRecord = RowFor<TTable>
  return new QueryBuilder<TRecord,TRecord[]>(table)
}

return {
  from,

  async rpc<T=unknown>(name:string,args:Record<string,unknown>={}):Promise<Result<T>>{
    clearSelectCache()
    try{
      if(!sendRpc)throw new Error('RPC aninhada não permitida.')
      const body=await sendRpc(name,args)
      return{data:(body?.data??body??null) as T,error:null}
    }catch(error){
      return{data:null,error:asError(error)}
    }
  },
}


}
